/**
 * Repository access tab listing OAuth connections used to sync repositories.
 */

import React from 'react';
import { ConnectionCard } from './ConnectionCard';
import type { OAuthConnection, Provider, PurposeType } from './types';

interface RepoProvidersTabProps {
  repoProviders: Provider[];
  failedAvatars: Set<string>;
  onAvatarError: (avatarUrl: string) => void;
  onConnect: (providerName: string, purpose: PurposeType) => void;
  onDisconnect: (
    connectionId: string,
    providerName: string,
    username: string,
    purpose: string,
  ) => void;
  onInstallGitHubApp: (installationUrl: string) => void;
}

export const RepoProvidersTab: React.FC<RepoProvidersTabProps> = ({
  repoProviders,
  failedAvatars,
  onAvatarError,
  onConnect,
  onDisconnect,
  onInstallGitHubApp,
}) => {
  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Repository Access</h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Connect providers to sync and access repositories.
        </p>
      </div>

      <div className="space-y-3">
        {repoProviders.map((provider) => (
          <div
            key={`repo-${provider.name}`}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8">{provider.icon}</div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {provider.displayName}
                    </h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 font-medium">
                      Repos
                    </span>
                    {provider.repoConnections.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({provider.repoConnections.length} connected)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    Sync repositories from {provider.displayName}
                  </p>
                </div>
              </div>

              <button
                onClick={() => onConnect(provider.name, 'repository')}
                className="px-3 py-1.5 text-sm bg-teal-600 dark:bg-teal-500 text-white rounded-md hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors flex items-center gap-1.5"
              >
                <span className="text-base">+</span>
                {provider.repoConnections.length === 0 ? 'Connect' : 'Add Another'}
              </button>
            </div>

            {provider.repoConnections.length > 0 && (
              <div className="space-y-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                {provider.repoConnections.map((connection: OAuthConnection) => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    provider={provider}
                    purposeLabel={
                      connection.purpose === 'both' ? 'Auth + Repositories' : 'Repositories only'
                    }
                    failedAvatars={failedAvatars}
                    onAvatarError={onAvatarError}
                    onConnect={onConnect}
                    onDisconnect={onDisconnect}
                    onInstallGitHubApp={onInstallGitHubApp}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
